import * as React from 'react';
import { useEffect } from 'react';
import Config from 'config';
import { Box } from '@apia/theme';
import { TComponentValidatorObj } from '../../../../types';
import IProperty from '../../../../JSApi/constants/IProperty';
import { setFieldFocusFromJSApi } from '../../../../store/JSApiSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { TRequestError } from '../../../../store/types';
import { getOutOfSyncFields, isArrayOfStrings } from '../../../../utils/util';
import {
  updateActionState,
  updateActionEvent,
  setGotoState,
  updateInvalidValues,
} from '../../../../store/appStateSlice';
import {
  IValuesSlice,
  processAllOutOFSyncFieldsSubmit,
  TApiaFieldValuesObj,
  TProcessSubmitAllPayload,
} from '../../../../store/valuesSlice';
import store from '../../../../store/index';

const updateInvalidFieldsState = (): string[] => {
  const valueState = store?.getState()?.valuesSlice;
  const requiredDomElements = document.body.querySelectorAll(
    '[required], [data-required]',
  );

  // grab requireds via dom as they come in x/y order by defualt including ones
  // inside grid
  const invalidValuesIds = Array.from(requiredDomElements)
    .filter((fieldDOM: Element) => {
      let id;
      if (fieldDOM instanceof HTMLInputElement) {
        const { type } = fieldDOM;
        id =
          type === IProperty.TYPE_RADIO
            ? fieldDOM.name
            : fieldDOM.id || fieldDOM.dataset?.fieldId;
      } else if (fieldDOM instanceof HTMLElement) {
        id = fieldDOM.id || fieldDOM.dataset?.fieldId;
      }
      if (id) {
        const filedValueState = valueState[id] as IValuesSlice;
        const v = filedValueState.valueState as TComponentValidatorObj;
        if (v?.isValid === false) return fieldDOM;
      }
      return null;
    })
    .map((fieldDOM) => {
      if (fieldDOM instanceof HTMLElement) {
        const id = fieldDOM.id || fieldDOM?.dataset?.fieldId;
        return id;
      }
      return null;
    });
  if (isArrayOfStrings(invalidValuesIds) && invalidValuesIds !== undefined) {
    store.dispatch(updateInvalidValues(invalidValuesIds as string[]));
    return invalidValuesIds as string[];
  }
  return [];
};

const isFormValid = (): boolean => {
  const invalidValues = updateInvalidFieldsState();
  console.log('invalidValues: ', invalidValues);
  return invalidValues.length === 0;
};

const getFirstInvalidField = (): string => {
  const invalidFieldList: string[] =
    store?.getState().appStateSlice.invalidValues;
  return invalidFieldList[0];
};

export interface IApiaEditGridPage {
  children: React.ReactNode;
}

const ApiaEditGridPage = ({ children }: IApiaEditGridPage): JSX.Element => {
  const dispatch = useAppDispatch();

  const actionState = useAppSelector(
    (state) => state.appStateSlice.actionState,
  );

  const actionEvent = useAppSelector(
    (state) => state.appStateSlice.actionEvent,
  );

  useEffect(() => {
    // next & confirm actions
    if (
      actionState &&
      (actionState === Config.FRM_ACTION_NEXT ||
        actionState === Config.FRM_ACTION_CONFIRM)
    ) {
      updateActionEvent('form-on-validation');
    }
    // previous action
    if (actionState === Config.FRM_ACTION_PREVIOUS) {
      dispatch(setGotoState(`${Config.FORM_GOTO_PREVIOUS_STEP_URL()}`));
    }
  }, [actionState, dispatch]);

  useEffect(() => {
    async function syncOutOfSyncFields(
      outOfSyncFields: (TApiaFieldValuesObj | null)[],
    ): Promise<TProcessSubmitAllPayload | TRequestError> {
      const syncResult = await dispatch(
        processAllOutOFSyncFieldsSubmit(outOfSyncFields),
      );
      if (processAllOutOFSyncFieldsSubmit.fulfilled.match(syncResult)) {
        const result = syncResult.payload;
        if (result !== null) {
          return result;
        }
      }
      return syncResult.payload as TRequestError;
    }

    if (
      actionEvent === 'form-on-validation' ||
      actionEvent === 'form-on-revalidate'
    ) {
      if (!isFormValid()) {
        // put focus on first required component empty
        const invalidRequiredFieldId = getFirstInvalidField();
        dispatch(
          setFieldFocusFromJSApi({
            focusFieldId: invalidRequiredFieldId,
          }),
        );
        updateActionState(null);
      } else if (actionEvent === 'form-on-validation') {
        updateActionEvent('form-on-submit-event');
      } else if (actionEvent === 'form-on-revalidate') {
        updateActionEvent('form-on-sync-fields');
      }
    }
    if (actionEvent === 'form-on-sync-fields') {
      const outOfSyncFields = getOutOfSyncFields();
      if (outOfSyncFields.length > 0) {
        const syncResult = syncOutOfSyncFields(outOfSyncFields);
        syncResult
          .then((syncData) => {
            const response = syncData as TProcessSubmitAllPayload['payload'];
            if (response !== null && response.success === true) {
              // all synced
              updateActionEvent('form-on-check-web-dav');
            }
          })
          .catch((err) => {
            console.warn(err);
            updateActionEvent('form-on-error');
          });
      } else {
        updateActionEvent('form-on-check-web-dav');
      }
    }
  }, [actionEvent, dispatch]);

  return <Box>{children}</Box>;
};

export default ApiaEditGridPage;
